import { Menu, MenuSection } from "../../types/menu";
import { ReduxState } from "../../types/reduxState";
import { type DeleteDishReducerAction } from "./dishReducer";
import { RestaurantSelectedAction } from "./restaurantReducer";

export type FetchMenusResult = {
  menus: Menu[];
  menuSections: MenuSection[];
  menuIdsInDisplayOrder: string[];
};

export type MenuReducerAction =
  | DeleteDishReducerAction // dishReducerAction is a type from dishReducer.ts
  | RestaurantSelectedAction
  | (
      | { type: "FETCHED_MENUS"; payload: FetchMenusResult } // payload is FetchMenusResult
      | {
          type: "SET_MENU";
          payload: { menu: Menu; menuSectionsOfMenu?: MenuSection[] };
        } // payload is menu
      | { type: "SET_MENU_SECTION"; payload: MenuSection } // update of a menu section
      | { type: "SET_MENU_DISPLAY_ORDER"; payload: string[] } // payload is menuIdsInDisplayOrder
      | { type: "DELETE_MENU"; payload: string } // payload is menuId
      | {
          type: "DELETE_MENU_SECTION";
          payload: { menuId: string; menuSectionId: string };
        }
    ); // payload is error message

export const menuReducer = (
  state: ReduxState["menu"] = {
    initialMenusLoading: true,
    menus: [],
    menuSectionsByMenuId: {},
    menuIdsInDisplayOrder: [],
  },
  action: MenuReducerAction
) => {
  switch (action.type) {
    case "RESTAURANT_SELECTED":
      return {
        ...state,
        initialMenusLoading: true,
      };
    case "SET_MENU":
      const { menu, menuSectionsOfMenu } = action.payload;
      const menuIndex = state.menus.findIndex((m) => m.id === menu.id);
      const newMenus = [...state.menus];
      const newMenuIdsInDisplayOrder = [...state.menuIdsInDisplayOrder];
      if (menuIndex === -1) {
        newMenus.unshift(menu);
        newMenuIdsInDisplayOrder.unshift(menu.id);
      } else {
        newMenus[menuIndex] = menu;
      }

      // update menuSectionsByMenuId only if the SET_MENU action payload contains menuSectionsOfMenu
      const updatedMenuSectionsByMenuId = menuSectionsOfMenu
        ? {
            ...state.menuSectionsByMenuId,
            [menu.id]: menuSectionsOfMenu,
          }
        : state.menuSectionsByMenuId;

      return {
        ...state,
        menus: newMenus,
        menuIdsInDisplayOrder: newMenuIdsInDisplayOrder,
        menuSectionsByMenuId: updatedMenuSectionsByMenuId,
      };
    case "SET_MENU_SECTION":
      const updatedMenuSection = action.payload;
      const menuSectionList =
        state.menuSectionsByMenuId[updatedMenuSection.menuId];
      const updatedMenuSectionList = menuSectionList.map((menuSection) => {
        return menuSection.id === updatedMenuSection.id
          ? updatedMenuSection
          : menuSection;
      });
      return {
        ...state,
        menus: touchMenu(state.menus, updatedMenuSection.menuId),
        menuSectionsByMenuId: {
          ...state.menuSectionsByMenuId,
          [updatedMenuSection.menuId]: updatedMenuSectionList,
        },
      };
    case "SET_MENU_DISPLAY_ORDER":
      return {
        ...state,
        menuIdsInDisplayOrder: action.payload,
      };
    case "FETCHED_MENUS":
      const { menus, menuSections, menuIdsInDisplayOrder } = action.payload;
      const menuSectionsByMenuId: { [menuId: string]: MenuSection[] } = {};
      // looping on menu to ensure we handle menus with no sections
      menus.forEach((menu) => {
        menuSectionsByMenuId[menu.id] = menuSections.filter(
          (menuSection) => menuSection.menuId === menu.id
        );
      });

      return {
        ...state,
        initialMenusLoading: false,
        menus,
        menuSectionsByMenuId,
        menuIdsInDisplayOrder,
      };

    case "DELETE_MENU":
      return {
        ...state,
        menus: state.menus.filter((menu) => menu.id !== action.payload),
        menuSectionsByMenuId: Object.keys(state.menuSectionsByMenuId).reduce(
          (acc, menuId) => {
            if (menuId !== action.payload) {
              acc[menuId] = state.menuSectionsByMenuId[menuId];
            }
            return acc;
          },
          {} as { [menuId: string]: MenuSection[] }
        ),
        menuIdsInDisplayOrder: state.menuIdsInDisplayOrder.filter(
          (menuId) => menuId !== action.payload
        ),
      };
    case "DELETE_MENU_SECTION":
      // while deleting a menu section,
      // - we remove the menu section for the given menu
      // - delete the menuSectionId from menuSectionsInDisplayOrder for the menu.

      const { menuId, menuSectionId: deletedMenuSectionId } = action.payload;

      const menusAfterMenuSectionDelete = state.menus.map((menu) => {
        return menu.id === menuId
          ? {
              ...menu,
              menuSectionIdsInDisplayOrder:
                menu.menuSectionIdsInDisplayOrder.filter(
                  (menuSectionId) => menuSectionId !== deletedMenuSectionId
                ),
            }
          : menu;
      });

      return {
        ...state,
        menus: touchMenu(menusAfterMenuSectionDelete, menuId),
        menuSectionsByMenuId: {
          ...state.menuSectionsByMenuId,
          [menuId]: state.menuSectionsByMenuId[menuId].filter(
            (menuSection) => menuSection.id !== deletedMenuSectionId
          ),
        },
      };

    case "DELETE_DISH":
      const dishIds = action.payload;
      // This is when dishes have been deleted and should be removed from all menu sections.
      const menuIdsContainingDishes = state.menus
        .filter(
          (menu) =>
            state.menuSectionsByMenuId[menu.id].filter((menuSection) =>
              menuSection.dishIdsInDisplayOrder.some((dishId) =>
                dishIds.includes(dishId)
              )
            ).length > 0
        )
        .map((menu) => menu.id);

      return {
        ...state,
        menus: touchMenus(state.menus, menuIdsContainingDishes),
        menuSectionsByMenuId: Object.keys(state.menuSectionsByMenuId).reduce(
          (acc, menuId) => {
            const menuSections = state.menuSectionsByMenuId[menuId];
            const updatedMenuSections = menuSections.map((menuSection) => {
              return {
                ...menuSection,
                dishIdsInDisplayOrder: menuSection.dishIdsInDisplayOrder.filter(
                  (dishIdInMenuSection) =>
                    !dishIds.includes(dishIdInMenuSection)
                ),
              };
            });
            return {
              ...acc,
              [menuId]: updatedMenuSections,
            };
          },
          {}
        ),
      };

    default:
      return state;
  }
};

// touch the modifiedAt timestamp of the menu
// this is slightly off since the timestamp would be a bit later
// than server ts, however for the FE this should be fine.
const touchMenus = (menus: ReduxState["menu"]["menus"], menuIds: string[]) =>
  menus.map((menu) => {
    return menuIds.includes(menu.id)
      ? { ...menu, modifiedAtMillis: new Date().getTime() }
      : menu;
  });

const touchMenu = (menus: ReduxState["menu"]["menus"], menuId: string) =>
  touchMenus(menus, [menuId]);
